/**
 * לקוח — one customer, on the Control Plane. `/platform/tenants/{tenant_uid}`.
 *
 * 04 §13.2.1 bounds what may appear here in one sentence: *"View tenant metadata: name, status,
 * plan, counts, contacts. **Never business data**."* So this screen carries a status, two counts and
 * a subscription state, and there is no field on it that a company's numbers could reach.
 *
 * The COUNTS ARE NOT A LINK INTO THE OFFICE. A platform operator who wants to see inside a tenant
 * needs a support grant — an approved, time-bounded, always-audited thing (04 §13.1.5 / §6.4) — and
 * the absence of a "open this office" button here is that rule being structural rather than
 * remembered. OA-0001 §3.2 is the other half: GL-POINT must NOT be the one deciding which employee of
 * this customer may see Company X, so there is nothing on this screen that could.
 *
 * ── WHAT THIS SCREEN GAINED, AND WHY IT BELONGS HERE ────────────────────────────────────────────
 *
 * Suspension, reactivation and closure were server routes with no browser path, and this screen said
 * so in a written state — *"פעולות שטרם נפתחו בקונסולה"*. That state was honest and it was not a
 * product: an operator holding `platform.tenant.suspend` had no way to suspend anybody.
 *
 * They live HERE and not on the customer list, because each one is a decision about ONE named
 * customer and the operator has to be looking at that customer's card — its status, its counts, its
 * subscription — when they make it. A suspend button on a list row is a suspend button pressed on
 * the wrong row.
 *
 * ── EVERY ONE OF THEM IS A MODAL, AND THAT PASSES THE SIX TESTS ────────────────────────────────
 *
 * 09 §2.1: one decision (M3), one field — the reason (M2), no table (M4), no drill-down (M6),
 * nothing lost by pressing Escape (M5), and it changes state (M1). The blast radius is stated in
 * words before the click, and for suspension the sentence a platform operator most needs to be able
 * to give a customer is in it: **nothing is deleted**. OA-0001 §3.11.
 *
 * ── AND THE REFUSALS ARE NOT GENERIC ───────────────────────────────────────────────────────────
 *
 * The server answers a refused lifecycle move with the CURRENT status and the statuses the move is
 * permitted from. `lifecycleText.ts` turns those into "this customer is already suspended" and "the
 * action is possible from פעיל · תקופת ניסיון" — the difference between an operator who now knows
 * the job is done and an operator who opens a ticket about a working product.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * AND THEN THE OTHER HALF: WHAT HAPPENS WHEN THE CUSTOMER ACTUALLY LEAVES
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `platform.tenant.close` moves ONE COLUMN, and that is correct — *"a lifecycle change is a state
 * change and nothing else."* It is also only the commercial half. On the day a customer leaves, with
 * nothing but the three buttons above, their connector credential is still active on a machine
 * nobody watches and still reading their accounting database; their source connections still hold
 * live logins into it; every session is still live; and every unaccepted invitation still mints a
 * new authenticated principal. The offboarding surface is the half that closes those, and it belongs
 * on THIS card for the same reason the three buttons above do: it is a decision about one named
 * customer, and the operator has to be looking at that customer when they make it.
 *
 * ── THE THREE THINGS THIS SECTION EXISTS TO SAY ─────────────────────────────────────────────────
 *
 * 1. **Termination is not deletion.** Nothing is destroyed — the service's persistence port has no
 *    method that could. An operator who believes otherwise either avoids a correct action for months
 *    or takes a catastrophic one believing it can be undone, and both cost more than the sentence.
 *
 * 2. **What undoing costs is not a yes/no.** The server sends a UNION, and each member is a
 *    different conversation: free · until a stated instant · the window has passed and nothing is
 *    destroyed yet · NOT KNOWABLE because no policy can be read · gone. The fourth is the one a
 *    boolean would render as "cannot be undone", and it is not a refusal — it is an unanswered
 *    question with a named cause.
 *
 * 3. **There is no purge button, and the screen says why.** Deletion eligibility is a READ. There is
 *    no capability that authorises destroying a customer's data, therefore no route and no control.
 *    A missing button nobody explains reads as one somebody forgot.
 *
 * Nothing here decides permission, and nothing here computes a stage, a window or an eligibility. It
 * renders answers that arrived, through `offboardingText.ts`, and posts two writes.
 */

import { useCallback, useState } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { FactList } from '../components/FactList';
import { CreditControl } from '../components/CreditControl';
import {
  DocumentActions,
  DocumentSendRecords,
  billingEmailOrNull,
} from '../components/DocumentActions';
import { Modal } from '../components/Modal';
import { StateBlock, ErrorState } from '../components/StateBlock';
import { SetupProgressPanel } from '../components/SetupProgressPanel';
import { NextChargeEstimatePanel } from '../components/NextChargeEstimatePanel';
import { RecordHeader } from '../components/RecordHeader';
import { StatusChip } from '../components/StatusChip';
import { TabStrip } from '../components/TabStrip';
import { InfoNote } from '../components/InfoNote';
import { CapacityPanel } from '../components/CapacityPanel';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import type {
  ExportDispositionKind,
  OffboardingStateView,
  RevocationTally,
  TenantLifecycleRefusal,
  TenantStatus,
} from '../api/contract';
import { EXPORT_DISPOSITION_KINDS, TENANT_STATUSES } from '../api/contract';
import { formatInstantHe, subscriptionStateOrNoneHe, tenantStatusHe } from '../api/adminVocabulary';
import { documentStatusHe, paymentStageHe } from '../api/billingText';
import type { TenantLifecycleAction } from '../api/lifecycleText';
import { tenantLifecycleActionText, tenantLifecycleRefusalText } from '../api/lifecycleText';
import type { OffboardingAction } from '../api/offboardingText';
import {
  EXPORT_DISPOSITION_REQUIRED_HE,
  PURGE_HAS_NO_ACTION_HE,
  REINSTATEMENT_AUTHORITY_HE,
  RETENTION_ABSENT_HE,
  TERMINATION_DESTROYS_NOTHING_HE,
  TERMINATION_REVOKES,
  exportDispositionText,
  mustReestablishLines,
  offboardingActionText,
  offboardingRefusalOf,
  offboardingRefusalText,
  purgeText,
  retentionPolicyText,
  revocationLines,
  stageEnteredHe,
  stageHe,
  stageReasonHe,
  stageReversalText,
} from '../api/offboardingText';
import { ApiError, ConflictError, DeniedError, revocationTallyOf } from '../api/client';
import { callOperation } from '../api/operations';
import type { ExportDispositionBody } from '../api/operations';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { DenialNotice } from '../components/DenialNotice';
import type { DenyBody } from '../api/contract';
import { ServerRead } from '../components/ServerRead';
import { listOf, useServerData } from '../api/useServerData';
import { routes } from '../routes';

export interface PlatformTenantScreenProps {
  readonly tenantUid: string;
}

const TITLE_HE = 'כרטיס לקוח';

/**
 * NO PATH SEGMENTS. There used to be two tables here mapping an action to a URL fragment, and the
 * address was assembled from one of them at the call site. Both are gone: the address is now built
 * by `callOperation` from `registry/routes.baseline.json`, and each action names its OPERATION.
 *
 * The dispatch below is written as three (and two) separate `callOperation` calls rather than one
 * call taking a computed id, and that is deliberate. A computed id is invisible to the reachability
 * scan, so "the operator can suspend a customer" would rest on the same kind of inference the
 * screen-name map rested on. Spelled out, deleting the close control fails CI by itself.
 *
 * There is still no `purge` anywhere: `purge-eligibility` is a GET this screen reads, and nothing on
 * this surface can destroy anything.
 */

type Outcome =
  | { readonly kind: 'applied'; readonly action: TenantLifecycleAction }
  | { readonly kind: 'refused'; readonly refusal: TenantLifecycleRefusal }
  | { readonly kind: 'denied'; readonly body: DenyBody }
  | { readonly kind: 'error'; readonly requestUid: string }
  | { readonly kind: 'unreachable' };

/**
 * The offboarding outcomes, kept apart from the lifecycle ones above.
 *
 * `terminated` carries the tally because the counts ARE the answer to "what just happened", and
 * `reinstated` carries what must be re-established because *"reinstated: true" on its own would read
 * as a restoration, which is exactly what did not happen*.
 */
type OffboardingOutcome =
  | { readonly kind: 'terminated'; readonly revoked: RevocationTally | null }
  | { readonly kind: 'reinstated'; readonly mustReestablishHe: readonly string[] }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

/** Only the statuses the server actually sent, and only ones this build has Hebrew for. */
function knownStatuses(values: readonly string[] | undefined): readonly TenantStatus[] {
  const known: readonly string[] = TENANT_STATUSES;
  return (values ?? []).filter((v): v is TenantStatus => known.includes(v));
}

export function PlatformTenantScreen({ tenantUid }: PlatformTenantScreenProps): JSX.Element {
  const ctx = useWorkContext();

  /*
   * WHETHER THIS ADMINISTRATOR MAY MAIL A COPY OF A CUSTOMER'S DOCUMENT. **0054.**
   *
   * `platform.billing.document.resend` is granted to the platform owner alone. The control is
   * WITHHELD rather than offered and refused -- a button that always answers 403 teaches an
   * operator the product is broken, while an absent button teaches them they do not hold the
   * authority, which is the truth. The server decides again on the request either way: this is a
   * screen and not a gate.
   */
  const mayResendDocuments = (ctx.session?.capabilities ?? []).includes(
    'platform.billing.document.resend',
  );

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  return <PlatformTenantBody tenantUid={tenantUid} mayResendDocuments={mayResendDocuments} />;
}

/**
 * THE BODY, AND WHY IT IS A SEPARATE COMPONENT.
 *
 * Every hook on this screen lives here, BELOW the surface guard, because `ctx.surface` is `null`
 * until `auth.whoami.read` answers (`shell/ContextProvider.tsx:181`) and every in-app navigation is
 * a full page load. Declared in one component with the guard above them, the early return would run
 * on the first (unresolved) render and the hooks on the second — React then sees two different hook
 * orders for one component and throws DURING RENDER, and the arrival is a blank page.
 *
 * It is a SPLIT and not a hoist. `useServerData` has no `enabled` option
 * (`api/useServerData.ts:73`), so lifting the reads above the guard would issue a PLATFORM read on
 * an unresolved or wrong-surface render — a denied request, and an audited write, for a screen the
 * caller is not on. Here nothing is read until the session has been stated as PLATFORM, and each
 * component's hook order is unconditional.
 */
function PlatformTenantBody({
  tenantUid,
  /*
   * PASSED IN RATHER THAN READ HERE, and that is the hook-order rule this file's own header is
   * about. `useWorkContext` is already called in the component ABOVE the surface guard, where it
   * is unconditional; adding a second call here would be another hook in the component that holds
   * every read, for a value the parent already has. The gate itself is a plain boolean -- the
   * parent computes it from the session it already holds.
   */
  mayResendDocuments,
}: PlatformTenantScreenProps & { readonly mayResendDocuments: boolean }): JSX.Element {
  const [pendingAction, setPendingAction] = useState<TenantLifecycleAction | null>(null);
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [pendingOffboarding, setPendingOffboarding] = useState<OffboardingAction | null>(null);
  const [offboardingReason, setOffboardingReason] = useState('');
  // NO DEFAULT. An omitted export disposition would silently become a waiver on the wire, and a
  // right that disappears when a field is omitted is not a right.
  const [exportKind, setExportKind] = useState<ExportDispositionKind | null>(null);
  const [exportDetail, setExportDetail] = useState('');
  const [offboardingOutcome, setOffboardingOutcome] = useState<OffboardingOutcome | null>(null);

  /*
   * READ FROM THE SERVER. **Phase 2 §6, vertical D.**
   *
   * Two reads, because they answer two questions:
   *
   *   · `platform.tenant.read` supplies the customer record. The card used to find it in
   *     `fixtureTenants`, which meant every wired lifecycle write on this screen — suspend, close,
   *     reactivate, terminate, reinstate — was performed against a uid from a local constant.
   *   · `platform.tenant.offboarding.read` supplies the STAGE, the reversal window and the
   *     retention policy. The screen said so in a comment — "with apps/api running this is
   *     platform.tenant.offboarding.read" — and now it is.
   *
   * The retention policy is the one that must never be approximated: 0011 gives it NO DEFAULT day
   * counts, and `retentionPolicyText` renders a missing policy as the absence it is. A fixture there
   * put invented retention periods in front of an operator about to terminate a customer.
   */
  const readTenants = useCallback(() => callOperation('platform.tenant.read', {}), []);
  const readOffboarding = useCallback(
    () =>
      callOperation('platform.tenant.offboarding.read', { params: { tenant_uid: tenantUid } }),
    [tenantUid],
  );
  const tenantsRead = useServerData(readTenants);
  const offboardingRead = useServerData(readOffboarding);
  /*
   * ── TWO MORE READS, BECAUSE THE HEADER THE OWNER ASKED FOR NAMES WHAT THEY ANSWER. ────────
   *
   *     *"בראש המסך: סיכום קומפקטי — שם הלקוח, מצב המנוי, החבילה, המחיר, סיום הניסיון,
   *     מועד החיוב הבא ומצב התשלום. נתון שלא התקבל יוצג כחסר, בלי לנחש."*
   *
   * `platform.tenant.read` carries the customer record and the subscription STATE; it carries no
   * plan, no period and no trial. Those are `platform.subscription.read`, which the subscription
   * screen has always called -- the ledger in `reachability.ts` names that screen and is satisfied
   * by it, and a route may be reached from more than one place.
   *
   * THE QUOTAS COME FROM THE SAME ROUTE THE PANEL ALWAYS USED. Nothing is recomputed here, which is
   * the rule that stopped the office's own quota panel being a sentence with no read behind it.
   */
  const readSubscription = useCallback(
    () => callOperation('platform.subscription.read', { params: { tenant_uid: tenantUid } }),
    [tenantUid],
  );
  const readCapacity = useCallback(
    () => callOperation('platform.capacity.read', { params: { tenant_uid: tenantUid } }),
    [tenantUid],
  );
  /*
   * ── WHERE THIS CUSTOMER'S MONEY STANDS. **2026-11-02.** ───────────────────────────
   *
   *     *"מחיר המנוי, מועד החיוב הבא, מצב התשלום והחשבוניות … איני מבקש להשאיר
   *     אותם כ„חסר” באופן קבוע."*
   *
   * Three of the header's slots were rendered as MISSING because no route answered them. This is
   * that route: `platform.billing.history.read`, 08 §12B.3 row 10 — a capability that existed with
   * no path, which is the opposite of an unreachable action and just as invisible.
   *
   * IT COMPUTES NO PRICE. The amounts are what `billing_document` RECORDS was charged. What the
   * next period will cost is the billing console's due table, which runs the real mechanism in
   * measure-only mode — one pricing implementation, and this is not a second one.
   */
  const readStatement = useCallback(
    () => callOperation('platform.tenant.statement.read', { params: { tenant_uid: tenantUid } }),
    [tenantUid],
  );
  const statementRead = useServerData(readStatement);

  /**
   * WHAT WAS ALREADY SENT, AND WHERE A COPY WOULD GO BY DEFAULT. **0054, 2026-11-04.**
   *
   *     *"תעד מי שלח, מתי, לאיזו כתובת ומה תוצאת השליחה בכרטיס הלקוח."*
   *
   * A SEPARATE READ FROM THE STATEMENT, AND DELIBERATELY SO. The statement is ONE answer served to
   * both this card and the office's own invoices tab, *"so the operator and the customer cannot be
   * told different things about the same documents."* This one names which PLATFORM administrator
   * mailed a copy of the customer's invoice and to which address — which the customer has no
   * business reading about our staff, so it could not join the shared answer.
   *
   * ONE REQUEST FOR THE WHOLE TAB. Most documents have never been resent; a per-row read would be
   * a request per invoice for a record that usually does not exist.
   */
  const readSendHistory = useCallback(
    () => callOperation('platform.tenant.document.history', { params: { tenant_uid: tenantUid } }),
    [tenantUid],
  );
  const sendHistoryRead = useServerData(readSendHistory);

  const readPriceBook = useCallback(
    () => callOperation('platform.subscription.price_book.read', { params: { tenant_uid: tenantUid } }),
    [tenantUid],
  );
  const subscriptionRead = useServerData(readSubscription);
  const capacityRead = useServerData(readCapacity);
  const priceBookRead = useServerData(readPriceBook);

  const tenant = listOf(tenantsRead.result, 'tenants').find((t) => t.tenantUid === tenantUid) ?? null;
  const back = { labelHe: 'הלקוחות', href: routes.platformTenants };

  if (tenantsRead.result.state !== 'loaded') {
    /*
     * NOT YET AN ANSWER, so not yet "unavailable".
     *
     * `tenant` is derived by searching the list, and an absent list yields `null` — which is the
     * same value a genuinely unknown uid yields. Falling into the block below while the read is
     * still outstanding would tell an operator the customer does not exist because the page had not
     * finished loading; falling into it after a refusal would say the same thing about a customer
     * this operator merely may not see.
     */
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <ServerRead result={tenantsRead.result} whatHe="את פרטי הלקוח" skeletonRows={6}>
          {() => null}
        </ServerRead>
      </AppShell>
    );
  }

  if (tenant === null) {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        {/*
          09 §5.8: not a raw 404 and not a blank screen. The wording is careful not to confirm or
          deny that a tenant with this uid exists anywhere — that is the gate's disclosure rule, and
          a screen that leaked it would undo it. It is reached ONLY after a successful read, so it
          is a statement about the uid and never about the request.
        */}
        <StateBlock
          titleHe="הלקוח הזה אינו זמין"
          bodyHe="לא הצלחנו להציג את הלקוח שאליו מפנה הקישור."
          tone="unavailable"
          nextStep={
            <a className="btn btn--nav" href={back.href}>
              חזרה לרשימת הלקוחות
            </a>
          }
        />
      </AppShell>
    );
  }

  const status = tenantStatusHe(tenant.status);
  const subscription = subscriptionStateOrNoneHe(tenant.subscriptionState);

  function closeModal(): void {
    setPendingAction(null);
    setReason('');
  }

  async function apply(action: TenantLifecycleAction): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      // A write. Never retried — the client cannot know whether the server committed, and a repeated
      // suspension is a second audit row against a customer who was suspended once.
      const params = { tenant_uid: tenantUid };
      if (action === 'suspend') {
        await callOperation('platform.tenant.suspend', { params, body: { reason } });
      } else if (action === 'reactivate') {
        await callOperation('platform.tenant.reactivate', { params, body: { reason } });
      } else {
        await callOperation('platform.tenant.close', { params, body: { reason } });
      }
      setOutcome({ kind: 'applied', action });
    } catch (err: unknown) {
      if (err instanceof ConflictError) {
        // The actionable half, as the server sent it. Nothing here decides what it means.
        setOutcome({
          kind: 'refused',
          refusal: {
            reason: err.body.error,
            currentStatus: knownStatuses(
              err.body.currentStatus === undefined ? [] : [err.body.currentStatus],
            )[0] ?? null,
            permittedFrom: knownStatuses(err.body.permittedFrom),
          },
        });
      } else if (err instanceof DeniedError) {
        setOutcome({ kind: 'denied', body: err.body });
      } else if (err instanceof ApiError && err.status === 0) {
        setOutcome({ kind: 'unreachable' });
      } else if (err instanceof ApiError) {
        setOutcome({ kind: 'error', requestUid: err.requestUid });
      } else {
        setOutcome({ kind: 'unreachable' });
      }
    } finally {
      setSending(false);
      closeModal();
    }
  }

  function closeOffboardingModal(): void {
    setPendingOffboarding(null);
    setOffboardingReason('');
    setExportKind(null);
    setExportDetail('');
  }

  /**
   * The two offboarding writes.
   *
   * The export disposition is BUILT FROM THE CHOICE and never defaulted — there is no `?? WAIVED`
   * here and there is no arm for "not answered", which is why the confirm control stays disabled
   * until one of the three has been selected and its detail supplied.
   */
  async function sendOffboarding(action: OffboardingAction): Promise<void> {
    setSending(true);
    setOffboardingOutcome(null);
    try {
      const params = { tenant_uid: tenantUid };
      if (action === 'reinstate') {
        const body = await callOperation('platform.tenant.offboarding.reinstate', {
          params,
          body: { reason: offboardingReason },
        });
        setOffboardingOutcome({
          kind: 'reinstated',
          mustReestablishHe: mustReestablishLines(body.mustReestablish),
        });
      } else {
        // NOT A `??`. An unanswered disposition does not become a waiver here, exactly as it does
        // not become one on the route: there is no arm for "not answered", so the request is simply
        // not built. The confirm control is guarded by the same condition.
        if (exportKind === null) return;
        const exportDisposition: ExportDispositionBody =
          exportKind === 'REQUESTED'
            ? { kind: 'REQUESTED' }
            : exportKind === 'ALREADY_DELIVERED'
              ? { kind: 'ALREADY_DELIVERED', exportRequestUid: exportDetail }
              : { kind: 'WAIVED', detail: exportDetail };
        const body = await callOperation('platform.tenant.offboarding.terminate', {
          params,
          body: { reason: offboardingReason, exportDisposition },
        });
        setOffboardingOutcome({ kind: 'terminated', revoked: revocationTallyOf(body.revoked) ?? null });
      }
    } catch (err: unknown) {
      // One classification, the same one every screen uses. No private `catch` vocabulary here.
      setOffboardingOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
      closeOffboardingModal();
    }
  }

  const modalCopy = pendingAction === null ? null : tenantLifecycleActionText(pendingAction);
  const refusalText = outcome?.kind === 'refused' ? tenantLifecycleRefusalText(outcome.refusal) : null;

  // From the server. `null` while the read is outstanding or after it failed — and the card below
  // renders that through `<ServerRead>` rather than as "this customer has no offboarding state",
  // which would read as "nothing has been terminated" about a customer that may have been.
  const offboarding: OffboardingStateView | null =
    offboardingRead.result.state === 'loaded' ? offboardingRead.result.data : null;
  const reversal = offboarding === null ? null : stageReversalText(offboarding.stage);
  const purge = offboarding === null ? null : purgeText(offboarding.purge);
  const retention = offboarding?.retention ?? null;
  const retentionText = retention === null ? null : retentionPolicyText(retention);
  /*
   * ── WHAT THE HEADER KNOWS, AND WHAT IT DOES NOT. ───────────────────────────────────
   *
   * `null` while a read is outstanding or after it failed, which is why every slot below declares
   * its own `absentHe`: a header that showed a blank where a plan should be would be making a
   * statement about the customer out of a request that never arrived.
   *
   * THREE OF THE SEVEN SLOTS THE OWNER NAMED HAVE NO PRODUCER ANYWHERE IN THIS PRODUCT: the price
   * AMOUNT, the next charge date and the payment state. There is no platform route that answers any
   * of them for one customer. They are rendered as MISSING and are not computed from anything
   * adjacent -- *"נתון שלא התקבל יוצג כחסר, בלי לנחש"* -- and the real facts that sit
   * NEXT to them, the pinned price book and the period end, are shown under their own names.
   */
  /*
   * `?? null` AND NOT A BARE FIELD READ, which the suites found within the hour. A 200 whose body
   * does not carry the field is not the same as a read that has not arrived -- and in this product
   * it is reachable, because a URL prefix can match a different route's stub. An undefined that
   * reaches a render is the defect this very round was opened by.
   */
  const planView =
    subscriptionRead.result.state === 'loaded'
      ? (subscriptionRead.result.data.subscription ?? null)
      : null;
  const pinnedBook =
    priceBookRead.result.state === 'loaded' ? (priceBookRead.result.data.pinned ?? null) : null;
  /*
   * A 200 WHOSE BODY DOES NOT CARRY THE SHAPE IS NOT AN ANSWER, which the suites found here within
   * the hour for the second time today: a URL prefix can match another route's stub, and an
   * undefined that reaches a render is the defect this whole round was opened by. `nextCharge` is
   * the field every branch below reads, so it is the field this asks for.
   */
  const statementBody =
    statementRead.result.state === 'loaded' ? statementRead.result.data : null;
  const statement =
    statementBody != null && statementBody.nextCharge !== undefined ? statementBody : null;
  const offboardingCopy =
    pendingOffboarding === null ? null : offboardingActionText(pendingOffboarding);
  const exportCopy = exportKind === null ? null : exportDispositionText(exportKind);
  const offboardingReady =
    offboardingReason.trim() !== '' &&
    (pendingOffboarding === 'reinstate' ||
      (exportCopy !== null && (exportCopy.detailLabelHe === null || exportDetail.trim() !== '')));

  return (
    <AppShell
      titleHe={tenant.legalName}
      backToParent={back}
    >
      {/*
        ══ THE FIXED HEADER THE INSTRUCTION ASKED FOR. **Corrected 2026-11-04.** ═══════════

        **The Owner:** *"פריסת כרטיס הלקוח לפי הצילומים וההנחיות שכבר נמסרו."*

        I HAD SAID I HAD NO REFERENCE FOR THIS CARD. That was wrong and it was checkable: the
        instruction is recorded, in his own words, on `RecordHeader` itself — *"בתוך כרטיס
        החברה כותרת קבועה עם שם ומספר רשמי, טאבים לפי נושא וקבוצות שדות מתקפלות"* — and three components
        exist for it. `TenantCompanyScreen` uses all three. THIS screen had the tabs and neither
        of the other two, so the pattern was half-applied, and the missing half is the one that
        says WHICH RECORD you are looking at.

        AND THE NUMBER WAS NOWHERE IN THE HEAD. `displayNo` is, in the contract's own words, *"the
        number a person reads out on the telephone"* — GLP-10001 — and it sat inside the overview
        TAB while the head carried a price and two dates. An operator on the phone had to choose a
        subject before the card would tell them who it was about. `null` renders *"טרם הוקצה"*
        through `RecordHeader`'s own named absence, never a blank.

        THE TWO SLOTS THE HEADER NOW CARRIES ARE GONE FROM THE LIST BELOW — *"בלי כפילויות"*.
        What remains is the five commercial facts, which is what that list is for.
      */}
      <RecordHeader
        titleHe={tenant.legalName}
        officialNumber={tenant.displayNo}
        officialNumberLabelHe="מספר לקוח"
        marks={<StatusChip labelHe={subscription.labelHe} tone={subscription.tone} />}
      />

      {/*
        ════════════════════════════════════════════════════════════════════════════════════
          THE SUMMARY, AND IT STAYS ABOVE THE STRIP. **Owner, 2026-11-02.**
        ════════════════════════════════════════════════════════════════════════════════════

            *"בראש המסך: סיכום קומפקטי … נתון שלא התקבל יוצג כחסר, בלי לנחש."*

        FIVE SLOTS NOW, in his order, and the two that left are stated by the `RecordHeader`
        above rather than deleted. `TabStrip`'s own rule says the record's header stays fixed
        above the strip -- an operator must be able to see WHICH customer and WHAT STATE without
        first choosing a subject, which is what the header is now for.
      */}
      <FactList
        facts={[
          {
            labelHe: 'החבילה',
            // The platform sells CODES -- 08 D12 -- and this is the platform's own screen.
            value: planView === null ? null : <span dir="ltr">{planView.planCode}</span>,
            absentHe: 'לא התקבלה תכנית',
          },
          {
            labelHe: 'ספר המחירים',
            value: pinnedBook === null ? null : <span dir="ltr">{pinnedBook.code}</span>,
            absentHe: 'לא הוצמד ספר מחירים',
          },
          {
            /*
             * THE LAST CHARGE, AND IT IS LABELLED AS ONE. A recorded figure with the period it
             * covered — not a forecast of the next period, which nothing in this product claims
             * to know before the period has ended and been measured.
             */
            labelHe: 'המחיר בחיוב האחרון',
            value:
              statement?.lastDocument == null ? null : (
                <span dir="ltr">
                  {statement.lastDocument.amountTotalDisplay ?? statement.lastDocument.amountTotal}
                </span>
              ),
            absentHe: statement === null ? 'לא התקבל' : 'טרם בוצע חיוב',
          },
          {
            labelHe: 'סיום הניסיון',
            value:
              planView === null || planView.trialEndsAt === null
                ? null
                : formatInstantHe(planView.trialEndsAt),
            absentHe: planView === null ? 'לא התקבל' : 'אין תקופת ניסיון',
          },
          {
            labelHe: 'סיום התקופה',
            value: planView === null ? null : formatInstantHe(planView.periodEnd),
            absentHe: 'לא התקבל',
          },
          {
            /*
             * DERIVED FROM THE MECHANISM, NOT FROM A SCHEDULE. `due()` selects `period_end < asOf`,
             * so the first run that can collect this period is the one on the day AFTER it ends —
             * and only while the state is billable, which is why the absent case names the state
             * rather than showing a date nothing will act on.
             */
            labelHe: 'מועד החיוב הבא',
            value:
              statement?.nextCharge.scheduled === true ? (
                <span dir="ltr">{statement.nextCharge.on}</span>
              ) : null,
            absentHe:
              statement === null
                ? 'לא התקבל'
                : statement.nextCharge.scheduled === false &&
                    statement.nextCharge.reason === 'no_subscription'
                  ? 'אין מנוי'
                  : 'אין חיוב מתוכנן במצב הנוכחי',
          },
          {
            labelHe: 'מצב התשלום',
            value:
              statement === null ? null : (
                <StatusChip
                  labelHe={
                    paymentStageHe(statement.payment.stage, statement.payment.governed).labelHe
                  }
                  tone={paymentStageHe(statement.payment.stage, statement.payment.governed).tone}
                />
              ),
            absentHe: 'לא התקבל',
          },
        ]}
      />

      {/*
        WHAT REPORTS A PROBLEM STAYS WITH THE HEADER, never behind a tab. `TabStrip`'s own rule:
        a warning that appears only if you happen to open the right tab is a warning the product
        cannot claim it gave.
      */}
      {outcome === null ? null : outcome.kind === 'applied' ? (
        <StateBlock
          titleHe="הפעולה בוצעה"
          bodyHe={`${tenantLifecycleActionText(outcome.action).intentHe} נרשמה ביומן יחד עם הסיבה ועם מי שביצע אותה.`}
          nextStep={<p>מצב הלקוח יתעדכן בכרטיס בטעינה הבאה.</p>}
        />
      ) : refusalText !== null ? (
        <section className="denial" data-tone="blocked" role="alert">
          <h2 className="denial__title">{refusalText.titleHe}</h2>
          <p className="denial__explanation">{refusalText.explanationHe}</p>
          <p className="denial__explanation">{refusalText.unchangedHe}</p>
          <p className="denial__next-step">{refusalText.nextStepHe}</p>
        </section>
      ) : outcome.kind === 'denied' ? (
        <DenialNotice deny={outcome.body} />
      ) : outcome.kind === 'error' ? (
        <ErrorState requestUid={outcome.requestUid} />
      ) : (
        <StateBlock
          titleHe="הפעולה לא בוצעה"
          bodyHe="לא הצלחנו ליצור קשר עם השרת, ולכן מצב הלקוח לא השתנה."
          tone="error"
          nextStep={<p>אפשר לנסות שוב מאוחר יותר.</p>}
        />
      )}

      {offboardingOutcome === null ? null : offboardingOutcome.kind === 'terminated' ? (
        <StateBlock
          titleHe="ההתקשרות הסתיימה"
          bodyHe={TERMINATION_DESTROYS_NOTHING_HE}
          tone="stale"
          nextStep={
            offboardingOutcome.revoked === null ? (
              <p>השרת לא מסר את מניין מה שנשלל, ולכן אינו מוצג כאן.</p>
            ) : (
              <ul className="notice__list">
                {revocationLines(offboardingOutcome.revoked).map((line) => (
                  <li key={line.classKey}>
                    {line.labelHe}: <span dir="ltr">{line.count}</span>
                  </li>
                ))}
              </ul>
            )
          }
        />
      ) : offboardingOutcome.kind === 'reinstated' ? (
        <StateBlock
          titleHe="ההתקשרות הוחזרה — והגישה לא הוחזרה"
          bodyHe="ההחזרה מחזירה את ההתקשרות בלבד. להלן מה שצריך להיעשות מחדש, ובלעדיו איש אינו נכנס."
          nextStep={
            offboardingOutcome.mustReestablishHe.length === 0 ? (
              <p>השרת לא מסר מה צריך להיעשות מחדש, ולכן אין כאן רשימה — אין להסיק מכך שדבר לא נדרש.</p>
            ) : (
              <ul className="notice__list">
                {offboardingOutcome.mustReestablishHe.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )
          }
        />
      ) : (
        <ActionFailureNotice
          failure={offboardingOutcome.failure}
          /*
           * THE FACTOR IS GIVEN HERE. **Owner section 3, 2026-10-27.**
           *
           * Without this handler `ActionFailureNotice` has no way to satisfy an MFA demand in
           * place and falls through to the arm that LINKS TO ANOTHER ADDRESS, which unmounts
           * this screen and loses whatever was half-entered. Every write this screen makes is
           * declared `per_action`, so the demand is a state it really reaches.
           *
           * NOTHING IS RE-SENT. `TenantUsersScreen` states the rule: *"A write that replays
           * itself after an authentication step is a write nobody pressed."* Clearing the
           * refusal puts the person back where they were, and they press again.
           */
          onStepUpSatisfied={() => setOffboardingOutcome(null)}
          // The conflict branch is the screen's own, because only this screen knows which route it
          // called. `offboardingRefusalText` is where the body becomes Hebrew; nothing is rendered raw.
          renderConflict={(failure) => {
            const text = offboardingRefusalText(offboardingRefusalOf(failure.body));
            return (
              <section className="denial" data-tone="blocked" role="alert">
                <h2 className="denial__title">{text.titleHe}</h2>
                <p className="denial__explanation">{text.explanationHe}</p>
                {/* Read this one first where it exists: the cascade already ran. */}
                {text.partialHe === null ? null : (
                  <p className="denial__explanation">{text.partialHe}</p>
                )}
                {text.revokedLines.length === 0 ? null : (
                  <ul className="notice__list">
                    {text.revokedLines.map((line) => (
                      <li key={line.classKey}>
                        {line.labelHe}: <span dir="ltr">{line.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {text.violationsHe.map((line) => (
                  <p key={line} className="denial__explanation">
                    {line}
                  </p>
                ))}
                {text.currentStageHe === null ? null : (
                  <p className="denial__explanation">{text.currentStageHe}</p>
                )}
                {text.commercialHe === null ? null : (
                  <p className="denial__explanation">{text.commercialHe}</p>
                )}
                {text.arcOwnerHe === null ? null : (
                  <p className="denial__explanation">{text.arcOwnerHe}</p>
                )}
                {text.reinstatableUntilHe === null ? null : (
                  <p className="denial__explanation">{`חלון ההחזרה הסתיים ב־${text.reinstatableUntilHe}.`}</p>
                )}
                {text.notBeforeHe === null ? null : (
                  <p className="denial__explanation">{`מחיקה מבוקרת אינה מתחילה לפני ${text.notBeforeHe}.`}</p>
                )}
                {text.unchangedHe === null ? null : (
                  <p className="denial__explanation">{text.unchangedHe}</p>
                )}
                <p className="denial__next-step">{text.nextStepHe}</p>
              </section>
            );
          }}
        />
      )}

      <TabStrip
        name="tenant"
        ariaLabelHe="כרטיס הלקוח"
        tabs={[
          {
            id: 'overview',
            labelHe: 'סקירה',
            render: () => (
              <>
              {/* THE WAY BACK INTO AN UNFINISHED SETUP. Shown here because this is the screen an operator
                  reaches by clicking the customer's name, and until 2026-10-20 it was a dead end for a
                  customer whose setup had stopped after the subscription. `licensedModules` is `null`:
                  this screen does not read the subscription's modules, and the panel says so rather than
                  reporting a step it cannot speak for. */}
              <SetupProgressPanel
                tenantUid={tenant.tenantUid}
                subscriptionState={tenant.subscriptionState}
                licensedModules={null}
                seatCount={tenant.seatCount}
              />

                <FactList
                  facts={[
                    { labelHe: 'מזהה ההקמה', value: <span dir="ltr">{tenant.slug}</span> },
                    {
                      labelHe: 'מצב הלקוח',
                      value: <StatusChip labelHe={status.labelHe} tone={status.tone} />,
                    },
                  ]}
                />

              {/*
                ── TWO DESTINATIONS, AND NEITHER IS A TAB. **Owner, 2026-10-23 §5.** ─────────────────────

                *"מנוי ומכסות ופעולות נוספות עדיין נקראות כלשוניות."* This was a row of two pills above a
                panel, which is the shape of a tab strip whatever the borders do — and one of them carried
                the plain action treatment while the other carried the navigation one, so the row did not
                even agree with itself about what it was.

                A destination now states where it goes AND what is there. A tab never does the second, which
                is what makes the two tellable apart before either is read.
              */}
              <nav aria-label="מסכים נוספים על הלקוח הזה">
                <p className="nav-cards__title">מסכים נוספים על הלקוח הזה</p>
                <ul className="nav-cards">
                  <li>
                    <a className="nav-card" href={routes.platformTenantSubscription(tenant.tenantUid)}>
                      <span className="nav-card__label">מנוי ומכסות</span>
                      <p className="nav-card__note">
                        התכנית, המודולים, מכסת החברות והמשתמשים, ותקופת הניסיון.
                      </p>
                    </a>
                  </li>
                  <li>
                    <a className="nav-card" href={routes.platformSupportGrants}>
                      <span className="nav-card__label">הרשאות תמיכה</span>
                      <p className="nav-card__note">
                        מי מטעם הפלטפורמה רשאי כרגע להיכנס לנתוני הלקוח, ובאיזו הרשאה.
                      </p>
                    </a>
                  </li>
                </ul>
              </nav>
              </>
            ),
          },
          {
            id: 'subscription',
            labelHe: 'מנוי ומכסות',
            render: () => (
              <>
                {/*
                  THE EDIT ACTION SITS BESIDE THE DATUM, which is the whole of the Owner's third
                  point. The writes themselves live on the subscription screen, where their
                  confirmation copy and their refusals already are; what moves here is the WAY IN,
                  on the row the number is on.
                */}
                <ServerRead
                  result={subscriptionRead.result}
                  whatHe="את המנוי של הלקוח"
                  skeletonRows={4}
                  renderNotFound={() => (
                    <StateBlock
                      titleHe="לא נמכר ללקוח הזה מנוי"
                      bodyHe="זה מצב תקין של לקוח בהקמה. הקמת מנוי נעשית במסך המנוי."
                      tone="empty"
                      nextStep={
                        <a className="btn btn--nav" href={routes.platformTenantSubscription(tenant.tenantUid)}>
                          למסך המנוי
                        </a>
                      }
                    />
                  )}
                >
                  {(data) =>
                    data.subscription == null ? (
                      <StateBlock
                        titleHe="לא נמכר ללקוח הזה מנוי"
                        bodyHe="הלקוח קיים וטרם נמכר לו כלום. זה שלב במכירה, לא תקלה."
                        tone="empty"
                      />
                    ) : (
                      <FactList
                        facts={[
                          { labelHe: 'התכנית', value: <span dir="ltr">{data.subscription.planCode}</span> },
                          {
                            labelHe: 'תקופה',
                            value: (
                              <span dir="ltr">
                                {formatInstantHe(data.subscription.periodStart)} —{' '}
                                {formatInstantHe(data.subscription.periodEnd)}
                              </span>
                            ),
                          },
                          { labelHe: 'אופן התקופה', value: data.subscription.periodKind },
                          {
                            labelHe: 'מודולים שנמכרו',
                            value:
                              data.subscription.licensedModules.length === 0 ? null : (
                                <span dir="ltr">{data.subscription.licensedModules.join(', ')}</span>
                              ),
                            absentHe: 'לא נמכרו מודולים',
                          },
                        ]}
                      />
                    )
                  }
                </ServerRead>

                <p className="actions-row">
                  <a className="btn btn--nav" href={routes.platformTenantSubscription(tenant.tenantUid)}>
                    עריכת המנוי, המכסות ותקופת הניסיון
                  </a>
                </p>

                {/*
                  THE QUOTAS, FROM THE ROUTE THAT MEASURES THEM. The same component the office sees
                  about itself, so the two surfaces cannot disagree about what "18 companies" means.
                */}
                <ServerRead
                  result={capacityRead.result}
                  whatHe="את המכסות"
                  skeletonRows={2}
                  renderNotFound={() => (
                    <StateBlock
                      titleHe="מכסות"
                      bodyHe="אין מנוי פעיל, ולכן אין מכסות להציג. זה אינו מכסה של אפס."
                      tone="unavailable"
                    />
                  )}
                >
                  {(data) => <CapacityPanel capacity={data.capacity} captionHe="מכסות שנרכשו" />}
                </ServerRead>
              </>
            ),
          },
          {
            id: 'billing',
            labelHe: 'חיובים וחשבוניות',
            render: () => (
              <>
                {/*
                  THE DOCUMENTS THIS CUSTOMER WAS CHARGED. **2026-11-02.**

                  The tab was an honest empty page for one day, because no platform route answered
                  one customer's documents. `platform.billing.history.read` — 08 §12B.3 row 10 —
                  had existed as a capability with no path since the capability set was seeded.

                  FOUR SENTENCES, NOT ONE EMPTY TABLE. The read may be loading or failed, the
                  office may never have been charged, and a charge may be recorded with no document
                  yet — and each of those is a different thing to tell an operator.

                  ── THE DOWNLOAD IS HERE NOW, AND THE RULE IT BROKE IS KEPT IN SIGHT ──────

                  This block used to say: *"NO DOWNLOAD HERE, AND THAT IS THE MASTER'S LINE.
                  §12B.1 gives the platform 'view billing & subscription history' and gives the
                  DOWNLOAD to the tenant: fetching somebody's tax document as an operator is a row
                  the master does not grant."*

                  That was a correct reading of §12B.1 and a wrong assumption about who settles
                  it. The Owner settled it the other way on 2026-11-04, in his own words:
                  *"ליד כל חשבונית יהיו שלוש פעולות למנהל בעל הרשאה מתאימה."* The superseded
                  reading is printed rather than deleted, so nobody finds the old rule in one file
                  and this table in another and has to guess which one is current.

                  THE RESEND IS A DIFFERENT AUTHORITY from the two reads, and its control is
                  WITHHELD rather than offered and refused: 0054's
                  `platform.billing.document.resend` is granted to the platform owner alone, and a
                  button that always answers 403 teaches an operator the product is broken.
                */}
                <ServerRead
                  result={statementRead.result}
                  whatHe="את החיובים של הלקוח"
                  skeletonRows={4}
                  renderNotFound={() => (
                    <StateBlock
                      titleHe="הלקוח הזה אינו מוכר"
                      bodyHe="המזהה שבכתובת אינו של לקוח קיים."
                      tone="unavailable"
                    />
                  )}
                >
                  {(data) =>
                    data.documents.length === 0 ? (
                      <StateBlock
                        titleHe="טרם בוצע חיוב"
                        bodyHe="לא נרשם עדיין אף חיוב ללקוח הזה. תקופה נגבית רק אחרי שהסתיימה."
                        tone="empty"
                      />
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th scope="col">תאריך</th>
                            <th scope="col">מספר מסמך</th>
                            <th scope="col">תקופת חיוב</th>
                            <th scope="col">סכום כולל מע״מ</th>
                            <th scope="col">מצב</th>
                            {/*
                              0049. THE ACT BESIDE THE DATUM IT ACTS ON — the Owner's own layout
                              requirement for this card: *"פעולת עריכה ליד הנתון הרלוונטי."*
                            */}
                            <th scope="col">פעולה</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.documents.map((doc) => {
                            const state = documentStatusHe(doc.status);
                            return (
                              <tr key={doc.documentUid}>
                                <td>
                                  <span dir="ltr">{formatInstantHe(doc.createdAt)}</span>
                                </td>
                                <td>
                                  {/* NULL UNTIL THE PROVIDER ISSUES ONE. A number we invented
                                      would be a document we invented. */}
                                  {doc.documentNumber === null ? (
                                    <span>טרם הופק</span>
                                  ) : (
                                    <span dir="ltr">{doc.documentNumber}</span>
                                  )}
                                </td>
                                <td>
                                  {doc.periodStart === null || doc.periodEnd === null ? (
                                    <span>אין תקופה</span>
                                  ) : (
                                    <span dir="ltr">{`${doc.periodStart} — ${doc.periodEnd}`}</span>
                                  )}
                                </td>
                                <td>
                                  <span dir="ltr">{doc.amountTotalDisplay ?? doc.amountTotal}</span>
                                </td>
                                <td>
                                  <StatusChip labelHe={state.labelHe} tone={state.tone} />
                                  {doc.providerFailureReason === null ? null : (
                                    <p className="notice__body">{doc.providerFailureReason}</p>
                                  )}
                                </td>
                                <td>
                                  {/*
                                    ONLY AN ISSUED INVOICE IS CREDITABLE, and the control is
                                    WITHHELD rather than offered and refused: the service answers
                                    `not_creditable` for a receipt or a credit and
                                    `original_not_issued` for one the provider never produced, and
                                    a button that always fails teaches an operator the product is
                                    broken.
                                  */}
                                  {doc.documentKind === 'invoice' && doc.status === 'issued' ? (
                                    <CreditControl
                                      tenantUid={tenantUid}
                                      documentUid={doc.documentUid}
                                      onIssued={() => statementRead.reload()}
                                    />
                                  ) : null}
                                  {/*
                                    ── THE THREE ACTIONS, ON AN ISSUED DOCUMENT WITH A NUMBER ─────

                                    THE SAME PREDICATE THE SERVER USES, so the screen withholds
                                    exactly what the service would refuse: `status === 'issued'`
                                    AND a number present. 0036's CHECK makes those two agree, and
                                    reading both is what keeps this from depending on a constraint
                                    in another schema to avoid offering an action on a document
                                    with no number.

                                    *"הצג במפורש אם החשבונית טרם הופקה"* — so the alternative is
                                    a SENTENCE and not an empty cell. An empty cell is what a
                                    reader sees when a feature is broken.
                                  */}
                                  {doc.status === 'issued' && doc.documentNumber !== null ? (
                                    <DocumentActions
                                      tenantUid={tenantUid}
                                      documentUid={doc.documentUid}
                                      documentNumber={doc.documentNumber}
                                      billingEmail={billingEmailOrNull(sendHistoryRead.result)}
                                      canResend={mayResendDocuments}
                                      onSent={() => sendHistoryRead.reload()}
                                    />
                                  ) : (
                                    <p className="notice__body">
                                      {doc.status === 'pending_issue'
                                        ? 'המסמך טרם הופק, ולכן אין מה להציג או לשלוח.'
                                        : 'אין מסמך מקורי שאפשר לשלוח במצב הזה.'}
                                    </p>
                                  )}
                                  {/*
                                    THE RECORD, BESIDE THE DOCUMENT IT IS ABOUT. The Owner asked
                                    for it in the customer card, and this is that place — not a
                                    separate audit screen an operator has to go and find.
                                  */}
                                  <DocumentSendRecords
                                    read={sendHistoryRead.result}
                                    documentUid={doc.documentUid}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )
                  }
                </ServerRead>

                {/*
                  ── WHAT THE NEXT PERIOD WOULD COST. **2026-11-03, item 3.** ───────────────
                  THE SAME PANEL THE OFFICE SEES, under the operator's own capability, so the two
                  cannot be looking at two different figures for the same month. It is a
                  PROJECTION and the panel says so in words -- the figure an operator may act on
                  is the due table's, which runs the real mechanism over a period that has ENDED.
                */}
                <NextChargeEstimatePanel tenantUid={tenantUid} />

                <p className="actions-row">
                  <a className="btn btn--nav" href={routes.platformBillingRun}>
                    למסך ביצוע החיובים
                  </a>
                </p>
              </>
            ),
          },
          {
            id: 'people',
            labelHe: 'משתמשים וחברות',
            render: () => (
              <>
                <FactList
                  facts={[
                    { labelHe: 'חברות בארגון', value: <span dir="ltr">{tenant.companyCount}</span> },
                    { labelHe: 'משתמשים בארגון', value: <span dir="ltr">{tenant.seatCount}</span> },
                  ]}
                />
                <InfoNote summaryHe="מה המספרים האלה סופרים">
                  <p>
                    המספרים סופרים את מה שקיים בארגון, לא את מה שנמכר לו. מה שנמכר מופיע בלשונית
                    „מנוי ומכסות”, והפער ביניהם הוא מצב תקין: תכנית שירדה מורידה מכסה ואינה
                    מוחקת דבר.
                  </p>
                </InfoNote>
                <p className="actions-row">
                  <a className="btn btn--nav" href={routes.platformSupportGrants}>
                    הרשאות התמיכה של הלקוח הזה
                  </a>
                </p>
              </>
            ),
          },
          {
            id: 'history',
            labelHe: 'היסטוריה',
            render: () => (
              <>
              {/*
                THE OFFBOARDING CARD, AND WHY ITS FAILURE STATE IS NOT A SENTENCE ABOUT THE CUSTOMER.

                This block used to render one "unavailable" message for every reason the state was absent.
                That was honest as far as it went, but it is the wrong grain HERE: an operator standing in
                front of this card is deciding whether to terminate a customer, and "we could not read the
                stage" and "you may not read the stage" send them to two different people. `<ServerRead>`
                keeps them apart, and neither of them is ever rendered as a stage.
              */}
              <ServerRead
                result={offboardingRead.result}
                whatHe="את מצב תהליך הסיום"
                skeletonRows={4}
                renderNotFound={() => (
                  <StateBlock
                    titleHe="מצב תהליך הסיום אינו זמין"
                    bodyHe="לא הצלחנו להציג באיזה שלב נמצא הלקוח הזה בתהליך הסיום, ולכן אין כאן קביעה לגבי החזרה."
                    tone="unavailable"
                    nextStep={<p>אפשר לרענן את המסך ולנסות שוב.</p>}
                  />
                )}
              >
                {(state) => (
                <section className="notice" data-tone="info">
                  <h2 className="notice__title">היכן הלקוח הזה עומד</h2>
                  <FactList
                    facts={[
                      {
                        labelHe: 'שלב בתהליך הסיום',
                        value: (
                          <StatusChip
                            labelHe={stageHe(state.stage.stage).labelHe}
                            tone={stageHe(state.stage.stage).tone}
                          />
                        ),
                      },
                      {
                        labelHe: 'מה עולה החזרה מכאן',
                        value:
                          reversal === null ? null : (
                            <StatusChip labelHe={reversal.labelHe} tone={reversal.tone} />
                          ),
                        absentHe: 'אין מה להחזיר — ההתקשרות פעילה',
                      },
                      {
                        // Shown ONLY where the server stated one. A window with no end is not a window, and
                        // an invented "בקרוב" here would be the product guessing at a governed instant.
                        labelHe: 'מועד אחרון להחזרה',
                        value: reversal?.deadlineHe ?? null,
                        absentHe: 'אין מועד — ראו ההסבר שלהלן',
                      },
                      {
                        labelHe: 'הכניסה לשלב',
                        value: state.stage.stage === 'ACTIVE' ? null : stageEnteredHe(state.stage),
                        absentHe: 'לא נרשם מעבר שלב',
                      },
                      {
                        labelHe: 'הסיבה שנרשמה',
                        value: stageReasonHe(state.stage),
                        absentHe: 'לא נרשמה סיבה',
                      },
                    ]}
                  />
                  <p className="notice__body">{stageHe(state.stage.stage).explanationHe}</p>
                  {reversal === null ? null : (
                    <>
                      <p className="notice__body">{reversal.explanationHe}</p>
                      {reversal.unknownHe === null ? null : (
                        <p className="notice__body">{reversal.unknownHe}</p>
                      )}
                      {reversal.restoresHe === null ? null : (
                        <p className="notice__body">{reversal.restoresHe}</p>
                      )}
                    </>
                  )}

                  {state.stage.stage !== 'TERMINATED' ? null : (
                    <>
                      <h3 className="notice__subtitle">מה נשלל בפועל</h3>
                      <ul className="notice__list">
                        {revocationLines(state.stage.revoked).map((line) => (
                          <li key={line.classKey}>
                            {line.labelHe}: <span dir="ltr">{line.count}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </section>
                )}
              </ServerRead>

              {offboarding === null ? null : retention === null || retentionText === null ? (
                <StateBlock
                  titleHe={RETENTION_ABSENT_HE.titleHe}
                  bodyHe={RETENTION_ABSENT_HE.bodyHe}
                  tone="unavailable"
                  nextStep={<p>עד שתוגדר מדיניות שמירה, בקשת סיום התקשרות לארגון הזה תידחה.</p>}
                />
              ) : (
                <section className="notice" data-tone="info">
                  <h2 className="notice__title">מדיניות השמירה שחלה על הלקוח</h2>
                  <FactList
                    facts={[
                      { labelHe: 'על סמך מה נקבעה', value: retention.basis },
                      { labelHe: 'חלון ההחזרה', value: retentionText.reinstatementWindowHe },
                      { labelHe: 'מתי מותר להתחיל במחיקה', value: retentionText.deletionEligibilityHe },
                      {
                        labelHe: 'מיקום הנתונים',
                        value: retentionText.residencyHe,
                        absentHe: 'המדיניות אינה מציינת מיקום נתונים שאפשר לקרוא',
                      },
                      {
                        labelHe: 'נקבעה בתאריך',
                        value: retentionText.setAtHe,
                        absentHe: 'לא נרשם מועד',
                      },
                    ]}
                  />
                  {retentionText.violationsHe.length === 0 ? null : (
                    <>
                      <h3 className="notice__subtitle">מה אינו תקין במדיניות</h3>
                      {/* EVERY reason, never the first: an operator fixing one at a time asks twice. */}
                      <ul className="notice__list">
                        {retentionText.violationsHe.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </section>
              )}

              {purge === null ? null : (
                <section className="notice" data-tone="info">
                  <h2 className="notice__title">{purge.titleHe}</h2>
                  <p className="notice__body">{purge.explanationHe}</p>
                  {purge.blockersHe.length === 0 ? null : (
                    <ul className="notice__list">
                      {purge.blockersHe.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  )}
                  <FactList
                    facts={[
                      {
                        labelHe: 'מוקדם מכך אין מתחילים',
                        value: purge.notBeforeHe,
                        absentHe: 'לא נמסר מועד',
                      },
                      {
                        labelHe: 'מה מחיקה הייתה משמידה',
                        value: purge.deletionImplicationHe,
                        absentHe: 'לא נמסר תיאור — התשובה נמסרת רק כשאין עוד התנגדות',
                      },
                    ]}
                  />
                  {/* No button, and the reason it is absent is on the screen rather than in a document. */}
                  <p className="notice__body">{PURGE_HAS_NO_ACTION_HE}</p>
                </section>
              )}
              </>
            ),
          },
        ]}
      />

      {/*
        ════════════════════════════════════════════════════════════════════════════════════
          פעולות מתקדמות — OUTSIDE THE STRIP, ON PURPOSE. **Owner, 2026-11-02.**
        ════════════════════════════════════════════════════════════════════════════════════

            *"השעיה, סיום התקשרות ומחיקה: באזור נפרד בשם „פעולות מתקדמות”.
            ההשלכות יוצגו כשבוחרים פעולה, לפני האישור."*

        AN AREA AND NOT A TAB, and the difference matters: a tab is a subject somebody chooses to
        read, and these are acts. Putting them in the strip would make "terminate this customer" a
        place you can arrive at by pressing the arrow key twice.

        NO BUTTON IS HIDDEN BY STATE, which is the rule the three here have always carried: a
        hidden control explains nothing, and an operator who believes a customer is already
        suspended must be able to find out that they are not. What each button does at the wrong
        moment is say so, by name.

        THE CONSEQUENCES ARE IN THE MODAL, where he asked for them -- `blastRadiusHe` names the
        customer and says what will NOT happen, before the confirm button is reachable. The standing
        explanation of the rules moves behind a marker, which is what `InfoNote` is for and is NOT
        where a consequence may go.
      */}
      <section className="notice" data-tone="warn">
        <h2 className="notice__title">פעולות מתקדמות</h2>
        <p className="notice__body">
          שלוש הפעולות האלה אינן מוחקות דבר. כל אחת מהן מציגה את ההשלכות שלה לפני האישור,
          ודורשת סיבה שנרשמת ביומן.
        </p>
        <div className="actions-row">
          <button type="button" className="btn" onClick={() => setPendingAction('suspend')}>
            {tenantLifecycleActionText('suspend').intentHe}
          </button>
          <button type="button" className="btn" onClick={() => setPendingAction('reactivate')}>
            {tenantLifecycleActionText('reactivate').intentHe}
          </button>
          <button
            type="button"
            className="btn btn--irreversible"
            onClick={() => setPendingAction('close')}
          >
            {tenantLifecycleActionText('close').intentHe}
          </button>
          <button
            type="button"
            className="btn btn--irreversible"
            onClick={() => setPendingOffboarding('terminate')}
          >
            {offboardingActionText('terminate').intentHe}
          </button>
          <button type="button" className="btn" onClick={() => setPendingOffboarding('reinstate')}>
            {offboardingActionText('reinstate').intentHe}
          </button>
        </div>

        <InfoNote summaryHe="מה כל פעולה עושה, ומה היא אינה עושה">
          <p>
            השעיה חוסמת פעולות ואינה מוחקת דבר: כל החברות, המשתמשים וההרשאות נשארים כפי שהם,
            והארגון ממשיך להופיע ברשימת הלקוחות כדי שאפשר יהיה להחזירו. סגירה מסיימת את ההתקשרות
            המסחרית, וגם היא אינה מוחקת נתונים.
          </p>
          <p>{TERMINATION_DESTROYS_NOTHING_HE}</p>
          <p>מה שנשלל בסיום התקשרות, לפי הסדר שבו הוא נשלל:</p>
          <ul className="notice__list">
            {TERMINATION_REVOKES.map((entry) => (
              <li key={entry.classKey}>
                <strong>{entry.labelHe}</strong> — {entry.detailHe}
              </li>
            ))}
          </ul>
          <p>{REINSTATEMENT_AUTHORITY_HE}</p>
        </InfoNote>
      </section>

      {offboardingCopy === null || pendingOffboarding === null ? null : (
        <Modal
          intentHe={offboardingCopy.intentHe}
          blastRadiusHe={`${offboardingCopy.intentHe} תחול על ${tenant.legalName}. ${offboardingCopy.blastRadiusHe}`}
          // Reason · disposition · the one detail a disposition may require. Never more.
          fields={pendingOffboarding === 'terminate' ? 3 : 1}
          irreversible={offboardingCopy.irreversible}
          confirmLabelHe={sending ? 'שולח…' : offboardingCopy.confirmLabelHe}
          onConfirm={() => {
            if (offboardingReady) void sendOffboarding(pendingOffboarding);
          }}
          onClose={closeOffboardingModal}
        >
          <label className="field">
            <span className="field__label">סיבת הפעולה</span>
            <input
              className="field__input"
              name="offboarding-reason"
              value={offboardingReason}
              onChange={(e) => setOffboardingReason(e.currentTarget.value)}
            />
            <span className="field__hint">{offboardingCopy.reasonHintHe}</span>
          </label>

          {pendingOffboarding !== 'terminate' ? null : (
            <>
              <fieldset className="field field--choice">
                <legend className="field__label">מה קורה עם הנתונים של הלקוח</legend>
                {EXPORT_DISPOSITION_KINDS.map((candidate) => (
                  <label key={candidate} className="choice">
                    <input
                      type="radio"
                      name="exportDisposition"
                      value={candidate}
                      checked={exportKind === candidate}
                      onChange={() => {
                        setExportKind(candidate);
                        setExportDetail('');
                      }}
                    />
                    <span>{exportDispositionText(candidate).labelHe}</span>
                  </label>
                ))}
                <span className="field__hint">{EXPORT_DISPOSITION_REQUIRED_HE}</span>
              </fieldset>

              {exportCopy === null ? null : (
                <p className="form__note">{exportCopy.hintHe}</p>
              )}

              {exportCopy === null || exportCopy.detailLabelHe === null ? null : (
                <label className="field">
                  <span className="field__label">{exportCopy.detailLabelHe}</span>
                  <input
                    className="field__input"
                    name="exportDispositionDetail"
                    value={exportDetail}
                    onChange={(e) => setExportDetail(e.currentTarget.value)}
                  />
                </label>
              )}
            </>
          )}

          {offboardingReady ? null : (
            <p className="form__note">
              הפעולה אינה נשלחת עד שהוזנה סיבה, ובסיום התקשרות — גם עד שנבחרה תשובה לשאלת הנתונים
              והושלם מה שהיא דורשת.
            </p>
          )}
        </Modal>
      )}

      {modalCopy === null || pendingAction === null ? null : (
        <Modal
          intentHe={modalCopy.intentHe}
          // 09 §2.2: the blast radius names the customer and says what will NOT happen.
          blastRadiusHe={`${modalCopy.intentHe} תחול על ${tenant.legalName}. ${modalCopy.blastRadiusHe}`}
          fields={1}
          irreversible={modalCopy.irreversible}
          confirmLabelHe={sending ? 'שולח…' : modalCopy.confirmLabelHe}
          onConfirm={() => void apply(pendingAction)}
          onClose={closeModal}
        >
          <label className="field">
            <span className="field__label">סיבת הפעולה</span>
            <input
              className="field__input"
              name="lifecycle-reason"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </label>
        </Modal>
      )}
    </AppShell>
  );
}
